import {
    positions,
    topPositions,
    rightPositions,
    horizontalPositions,
    verticalPositions,
} from "./constants";

/**
 * Verify if an element has scrollbar capability
 * @param {HTMLElement} element - HTML element
 * @returns {Boolean} - element is scrollable or not
 */
const isScrollableElement = (element) => {
    // Firefox wants us to check `-x` and `-y` variations as well
    const { overflow, overflowX, overflowY } = window.getComputedStyle(element);
    return /auto|scroll|overlay|hidden/.test(overflow + overflowY + overflowX);
};

/**
 * Recursive function to find the scrollable parent of an element
 * @param {HTMLElement} element - HTML element
 * @returns {element} - scrollable parent
 */
const getScrollParent = (element) => {
    if (["html", "body", "#document"].includes(element.nodeName.toLowerCase()))
        return element.ownerDocument.body;
    if (isScrollableElement(element)) return element;
    return getScrollParent(element.parentNode);
};

/**
 * Get the offsetParent of and element (offsetParent can be body or any element with positions relative, fixed, absolute, sticky, static)
 * @param {HTMLElement} element - HTML element
 * @returns {HTMLElement} - offsetParent element
 */
const getOffsetParent = (element) => {
    if (!element.offsetParent) return document.body;
    return element.offsetParent;
};

/**
 * Calculates the x and y positions relative to an offsetParent (body or any element with positions relative, fixed, absolute, sticky, static)
 * @param {HTMLElement} element - HTML element
 * @param {HTMLElement} offsetParent - HTML element to position element relative to
 * @returns {Object} - enhanced getBoundingClientRect with extra properties relative to an offsetParent
 */
export const getBoundingClientRectRelativeToOffsetParent = (element, offsetParent) => {
    const elementRect = element.getBoundingClientRect();

    // Get element scrollable container information
    // if element has a different scrollable container than body we will increment the scrollable container scrolls to the count
    // for now this only work with one scrollable parent (if you have 2 scrollable parents it will not work)
    const scroll = { scrollLeft: 0, scrollTop: 0 };
    const elementScrollableParent = getScrollParent(element);
    if (elementScrollableParent !== document.body && offsetParent === elementScrollableParent) {
        scroll.scrollLeft = elementScrollableParent.scrollLeft;
        scroll.scrollTop = elementScrollableParent.scrollTop;
    }

    // Get offsetParent offsets of the offsetParent
    const offsets = { x: 0, y: 0 };
    const offsetParentRect = offsetParent.getBoundingClientRect();
    offsets.x = offsetParentRect.x + offsetParent.clientLeft; // X + clientLeft(borders)
    offsets.y = offsetParentRect.y + offsetParent.clientTop; // X + clientTop(borders)

    return {
        top: elementRect.top,
        right: elementRect.right,
        bottom: elementRect.bottom,
        left: elementRect.left,
        width: elementRect.width,
        height: elementRect.height,
        x: elementRect.x,
        y: elementRect.y,
        xRelative: elementRect.left + scroll.scrollLeft - offsets.x, // Create relative measures
        yRelative: elementRect.top + scroll.scrollTop - offsets.y, // Create relative measures
    };
};

const topPositioning = (targetRect, elementRect, offsets) => {
    return targetRect.yRelative - elementRect.height - offsets.y;
};

const bottomPositioning = (targetRect, offsets) => {
    return targetRect.yRelative + targetRect.height + offsets.y;
};

const leftPositioning = (targetRect, elementRect, offsets) => {
    return targetRect.xRelative - elementRect.width - offsets.x;
};

const rightPositioning = (targetRect, offsets) => {
    return targetRect.xRelative + targetRect.width + offsets.x;
};

const verticalStartPositioning = (targetRect) => {
    return targetRect.xRelative;
};

const verticalMiddlePositioning = (targetRect, elementRect) => {
    const widthDif = Math.abs(targetRect.width - elementRect.width) / 2;
    return targetRect.width > elementRect.width
        ? targetRect.xRelative + widthDif
        : targetRect.xRelative - widthDif;
};

const verticalEndPositioning = (targetRect, elementRect) => {
    return targetRect.xRelative + targetRect.width - elementRect.width;
};

const horizontalStartPositioning = (targetRect) => {
    return targetRect.yRelative;
};

const horizontalMiddlePositioning = (targetRect, elementRect) => {
    const heightDif = Math.abs(targetRect.height - elementRect.height) / 2;
    return targetRect.height > elementRect.height
        ? targetRect.yRelative + heightDif
        : targetRect.yRelative - heightDif;
};

const horizontalEndPositioning = (targetRect, elementRect) => {
    return targetRect.yRelative + targetRect.height - elementRect.height;
};

export const topExceed = (rect, element) => {
    return rect.top - element.offsetHeight < 0;
};

export const topHalfExceed = (rect, element) => {
    return rect.top - element.offsetHeight / 2 < 0;
};

export const leftExceed = (rect, element) => {
    return rect.left - element.offsetWidth < 0;
};

export const leftHalfExceed = (rect, element) => {
    return rect.left - element.offsetWidth / 2 < 0;
};

export const bottomExceed = (rect, element) => {
    const windowHeight = document.documentElement.clientHeight || document.body.clientHeight;
    return rect.bottom + element.offsetHeight >= windowHeight;
};

export const bottomHalfExceed = (rect, element) => {
    const windowHeight = document.documentElement.clientHeight || document.body.clientHeight;
    return rect.bottom + element.offsetHeight / 2 >= windowHeight;
};

export const rightExceed = (rect, element) => {
    const windowWidth = document.documentElement.clientWidth || document.body.clientWidth;
    return rect.right + element.offsetWidth >= windowWidth;
};

export const rightHalfExceed = (rect, element) => {
    const windowWidth = document.documentElement.clientWidth || document.body.clientWidth;
    return rect.right + element.offsetWidth / 2 >= windowWidth;
};

export const collisionDetection = (target, element, position) => {
    const targetBoundingRect = target.getBoundingClientRect();

    // IsTopExceed
    const isTopExceed = topExceed(targetBoundingRect, element);
    const isRightExceed = rightExceed(targetBoundingRect, element);
    const isBottomExceed = bottomExceed(targetBoundingRect, element);
    const isLeftExceed = leftExceed(targetBoundingRect, element);

    // If fits, return position
    if (!isTopExceed && !isRightExceed && !isBottomExceed && !isLeftExceed) {
        return position;
    }

    const isTopHalfExceed = topHalfExceed(targetBoundingRect, element);
    const isRightHalfExceed = rightHalfExceed(targetBoundingRect, element);
    const isBottomHalfExceed = bottomHalfExceed(targetBoundingRect, element);
    const isLeftHalfExceed = leftHalfExceed(targetBoundingRect, element);

    // Mapping between position and boundary checks
    // {position: fits}
    // e.g. {"top-start": true} if fits, if doesn't fit {"top-start": false}
    const boundaries = {
        "top-start": !isRightExceed && !isTopExceed,
        top: !isLeftHalfExceed && !isTopExceed && !isRightHalfExceed,
        "top-end": !isLeftExceed && !isTopExceed,
        "right-start": !isRightExceed && !isBottomExceed,
        right: !isRightExceed && !isTopHalfExceed && !isBottomHalfExceed,
        "right-end": !isRightExceed && !isTopExceed,
        "bottom-start": !isBottomExceed && !isRightExceed,
        bottom: !isLeftHalfExceed && !isBottomExceed && !isRightHalfExceed,
        "bottom-end": !isLeftExceed && !isBottomExceed,
        "left-start": !isLeftExceed && !isBottomExceed,
        left: !isTopHalfExceed && !isLeftExceed && !isBottomHalfExceed,
        "left-end": !isTopExceed && !isLeftExceed,
    };

    // If fits, return position
    if (boundaries[position]) {
        return position;
    }

    // Positions array
    const availablePositions = Object.keys(boundaries);
    //Starting index
    const startingIndex = availablePositions.indexOf(position);
    // Distance from starting index
    let distance = availablePositions.length / 2;

    // From the opposite of the desired position, traversing left and right
    // return position when fits
    while (distance > 0) {
        const leftIndex =
            (startingIndex - distance + availablePositions.length) % availablePositions.length;
        const rightIndex = (startingIndex + distance) % availablePositions.length;
        if (boundaries[availablePositions[leftIndex]]) {
            return availablePositions[leftIndex];
        }
        if (boundaries[availablePositions[rightIndex]]) {
            return availablePositions[rightIndex];
        }
        // Decrease distance from desired position
        distance--;
    }

    // If nothing fits, return original position
    return position;
};

/**
 * Calculates the top and left values that an element needs to position itself relative to another
 * Important: Both target and element cannot be display: none at the time this funtion runs
 * @param {HTMLElement} target - anchor element
 * @param {HTMLElement} element - element to be positioned
 * @param {String} position - the position the element need to be positioned on the anchor
 * @param {Object} offsets - { x, y } offsets (x is applied to horizontal positions and y to vertical positions)
 * @returns {Object} - { top, left } cordinates to position element on target
 */
export const getPosition = (target, element, position, offsets = { x: 0, y: 0 }) => {
    // We need just the basic data around the element that will be positioned
    const elementRect = element.getBoundingClientRect();
    const elementOffsetParent = getOffsetParent(element);

    // Get target rect relative to the offsetParent of the element that must be positioned
    const targetRect = getBoundingClientRectRelativeToOffsetParent(target, elementOffsetParent);

    let top = 0;
    let left = 0;

    if (verticalPositions.includes(position)) {
        // Top/bottom positioning
        top = topPositions.includes(position)
            ? topPositioning(targetRect, elementRect, offsets)
            : bottomPositioning(targetRect, offsets);

        switch (position) {
            case positions.topStart:
            case positions.bottomStart:
                left = verticalStartPositioning(targetRect, elementRect);
                left -= offsets.x;
                break;
            case positions.top:
            case positions.bottom:
                left = verticalMiddlePositioning(targetRect, elementRect);
                break;
            case positions.topEnd:
            case positions.bottomEnd:
                left = verticalEndPositioning(targetRect, elementRect);
                left += offsets.x;
                break;
        }
    } else if (horizontalPositions.includes(position)) {
        // Left/right positioning
        left = rightPositions.includes(position)
            ? rightPositioning(targetRect, offsets)
            : leftPositioning(targetRect, elementRect, offsets);

        switch (position) {
            case positions.rightStart:
            case positions.leftStart:
                top = horizontalStartPositioning(targetRect, elementRect);
                top -= offsets.y;
                break;
            case positions.right:
            case positions.left:
                top = horizontalMiddlePositioning(targetRect, elementRect);
                break;
            case positions.rightEnd:
            case positions.leftEnd:
                top = horizontalEndPositioning(targetRect, elementRect);
                top += offsets.y;
                break;
        }
    } else {
        console.error("Choose a valid position");
    }

    top = Math.round(top);
    left = Math.round(left);

    return { top, left };
};
